// The core SDK. The default "mixpanel-browser" entry bundles the session-replay
// recorder (rrweb, ~300 KB) into every page, the login page included, while
// replay starts off and is switched on only after sign-in — see loadRecorder.
import mixpanel from "mixpanel-browser/dist/mixpanel-with-async-modules.cjs";
import { User } from "../stores/types";

/**
 * Mixpanel analytics for the dashboard.
 *
 * PRIVACY CONTRACT — read before adding a call.
 * This product handles children's records. Never pass to `track` or
 * `identifyUser`: child names, parent names, phone numbers, addresses,
 * GPS coordinates, notes, or anything free-text a user typed about a child.
 * Send counts, booleans, enum values, and opaque IDs instead. The one
 * deliberate exception is the *logged-in staff member's* own name on their
 * Mixpanel profile, so admins are identifiable in reports — staff, never a
 * child or a parent.
 *
 * Every export is a safe no-op when REACT_APP_MIXPANEL_TOKEN is unset (local
 * dev, CI, smoke runs) and never throws into calling code — analytics must
 * not be able to break a login or a child record write.
 */

const TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;
const IS_DEV = process.env.NODE_ENV === "development";

let ready = false;

/** Swallow SDK errors: a broken analytics call must never break a flow. */
const safely = (fn: () => void) => {
  if (!ready) return;
  try {
    fn();
  } catch (error) {
    if (IS_DEV) console.warn("[analytics] call failed:", error);
  }
};

/**
 * Initialize Mixpanel. Call once, before the app renders.
 *
 * No consent gate: the dashboard serves schools in the region, with no EU/EEA,
 * UK, or California users. If that changes, init with
 * `opt_out_tracking_by_default: true` and call `mixpanel.opt_in_tracking()`
 * from the consent prompt before any event fires.
 */
export const initAnalytics = (): void => {
  if (ready) return;

  if (!TOKEN) {
    if (IS_DEV) {
      console.info(
        "[analytics] REACT_APP_MIXPANEL_TOKEN not set — tracking disabled."
      );
    }
    return;
  }

  try {
    mixpanel.init(TOKEN, {
      // EU data residency. This project lives on eu.mixpanel.com, so ingestion
      // must go to the EU host — the default (api.mixpanel.com) silently
      // accepts events into a US-resident project you cannot see from the EU
      // UI. Tied to the token, not the environment: if the token is ever
      // swapped for a US-resident project, drop this line.
      api_host: "https://api-eu.mixpanel.com",
      debug: IS_DEV,
      // localStorage avoids the third-party-cookie restrictions that silently
      // drop events in Safari.
      persistence: "localStorage",
      track_pageview: "url-with-path",
      // Session replay starts OFF, and is switched on after sign-in when the
      // backend says so (applySessionReplay). What share of sessions is
      // recorded is a root admin's setting on Feature Flags, shared with the
      // mobile app — the free plan's replay allowance is one pool for both,
      // and once it is spent no recording can be watched until the month
      // resets. It was a hardcoded 100 here; the setting's default is still
      // 100, so nothing changed until someone lowers it.
      //
      // WHAT THIS CAPTURES: replay records the rendered DOM, so any screen an
      // admin opens is stored as shown. See the masking block below for which
      // parts of it are held back.
      record_sessions_percent: 0,
      // Console capture off. It defaults to true, and it is the one channel
      // verified to leak real data: with `debug` on, the SDK logs its own
      // profile payloads ($name, kindergarten_name) to the console and the
      // recorder stores them verbatim. The app's own console.error calls pass
      // caught error objects the same way. DOM text is masked by rrweb; console
      // output is not, so this is where the exposure actually was.
      record_console: false,
      // Masking is INVERTED from the SDK default. Out of the box rrweb masks
      // every text node, which makes replays unreadable; we turn that off so
      // Beta sessions can actually be followed, and mask the sensitive spots
      // by hand with the `mp-mask` class instead.
      //
      // Both lines are deliberate. Setting only the selector would flip
      // maskAll to false implicitly (masking.js:94) — stating it outright keeps
      // the intent visible to whoever reads this next.
      //
      // The tradeoff: anything NOT tagged is captured as rendered. When you add
      // a screen showing a name, phone, address, note, figure or credential,
      // tag it — see docs/MIXPANEL_TRACKING.md for the current list.
      record_mask_all_text: false,
      record_mask_text_selector: ".mp-mask",
      // Inputs stay masked regardless, so nothing typed is ever recorded.
      // Note this is the option the recorder actually reads; the documented
      // `record_mask_inputs` default is declared but never consumed.
      record_mask_all_inputs: true,
      // The dashboard is a hard-refresh-free SPA; flush promptly so Live View
      // reflects what just happened during verification.
      batch_flush_interval_ms: 2000,
    });
    ready = true;

    const superProps: Record<string, unknown> = { platform: "web_dashboard" };
    // Only when actually set — otherwise every event carries app_version:"unknown".
    if (process.env.REACT_APP_VERSION) {
      superProps.app_version = process.env.REACT_APP_VERSION;
    }
    mixpanel.register(superProps);
  } catch (error) {
    if (IS_DEV) console.warn("[analytics] init failed:", error);
  }
};

/** Track an event. Properties must be non-PII — see the privacy contract. */
export const track = (
  event: string,
  properties: Record<string, unknown> = {}
): void => {
  safely(() => mixpanel.track(event, properties));
};

/**
 * Bind the session to a user. Call on login, and on session restore
 * (checkAuth) so a returning admin's events attach to the same profile.
 */
export const identifyUser = (user: User, kindergartenName?: string): void => {
  safely(() => {
    mixpanel.identify(user.id);

    const profile: Record<string, unknown> = {
      // Staff member's own name — see the privacy contract above.
      $name: user.name,
      role: user.role,
      kindergarten_id: user.kindergarten_id || null,
    };
    // Session restore has the user but not always the settings. Writing null
    // here would wipe the name already on the profile, so only set it when
    // we genuinely have one.
    if (kindergartenName) profile.kindergarten_name = kindergartenName;
    mixpanel.people.set(profile);

    // Stamped on every subsequent event so any report can be sliced by who
    // and which school without a join. The school's name too, when known:
    // the free plan has no lookup tables, so a report can only show a school
    // by name if the name travels on the event (the app does the same).
    mixpanel.register({
      role: user.role,
      kindergarten_id: user.kindergarten_id || null,
      ...(kindergartenName ? { kindergarten_name: kindergartenName } : {}),
    });
  });
};

// The share the current recording decision was made for, and whether this
// page is being recorded. One coin per page load per answer: checkAuth runs
// on many route mounts, and re-tossing on each would start and stop the
// recording mid-session.
let replayDecidedFor: number | null = null;
let replayRecording = false;

/**
 * The recorder, as a chunk of our own, fetched the first time a session is
 * recorded. Running it sets `window.__mp_recorder`, which the core SDK looks
 * for before it would fetch the same file from Mixpanel's CDN — so it never
 * does, and the recorder is always the one installed with this SDK version.
 */
const loadRecorder = () =>
  import(
    /* webpackChunkName: "mixpanel-recorder" */ "mixpanel-browser/dist/mixpanel-recorder.min.js"
  );

/**
 * Record this session, or not, as the backend decided for this person
 * (`GET /system/session-replay-policy?surface=dashboard` — asked from
 * authStore after sign-in and session restore). `percent` is a chance: 100 is
 * every session, which is what a person a root admin asked to record gets,
 * and 0 — also the answer when the check fails — is none.
 */
export const applySessionReplay = (percent: number): void => {
  safely(() => {
    if (replayDecidedFor === percent) return;
    replayDecidedFor = percent;
    const record =
      percent >= 100 || (percent > 0 && Math.random() * 100 < percent);
    if (record && !replayRecording) {
      replayRecording = true;
      loadRecorder().then(
        () =>
          safely(() => {
            // Not if it was stopped, or the person logged out, meanwhile.
            if (replayRecording) mixpanel.start_session_recording();
          }),
        (error) => {
          replayRecording = false;
          if (IS_DEV) console.warn("[analytics] recorder failed to load:", error);
        }
      );
    } else if (!record && replayRecording) {
      mixpanel.stop_session_recording();
      replayRecording = false;
    }
  });
};

/**
 * Clear identity on logout. Required on shared machines — without it the next
 * person to log in on this browser inherits the previous admin's distinct_id.
 * Stops any recording first, so the next person's session is decided afresh.
 */
export const resetAnalytics = (): void => {
  safely(() => {
    if (replayRecording) {
      mixpanel.stop_session_recording();
      replayRecording = false;
    }
    replayDecidedFor = null;
    mixpanel.reset();
  });
};
